import { useState, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type BookingStatus, type DeliveryMethod } from '@/api/admin'
import { DeliveryMethodFields } from '@/components/shared/DeliveryMethodFields'
import { useIssueProgress, IssueProgressBar } from './IssueProgress'
import { CompletedAttendance, BookingHistoryCard, MissingCertificatesBanner } from './CompletedAttendance'
import { VenueSelector } from '@/features/admin/venues/VenueSelector'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Breadcrumb } from '@/components/shared/Breadcrumb'
import { useTopBar } from '@/contexts/TopBarContext'
import { Check, AlertTriangle, UserCheck, Users, Radio, Award, CalendarCheck, CheckCircle2, Ban, XCircle, ChevronDown, Star, Plus, Trash2, Eye, Download, Upload } from 'lucide-react'
import { NoEligibleTrainers } from '@/components/shared/NoEligibleTrainers'
import { TrainerAssignmentFields, type TrainerAssignment } from '@/components/shared/TrainerAssignmentFields'
import ThirdPartyCertificateUploader from '@/features/admin/certificates/ThirdPartyCertificateUploader'
import { certificateDownloadUrl, certificateViewUrl } from '@/api/public'
import type { BookingAttendeeDto, BookingAttendeeCertificateDto } from '@/api/admin'
import { useAdminDates } from '@/hooks/useDates'
import { LiveRoomCard } from '@/features/live/LiveRoomCard'
import { useLiveStatus } from '@/features/live/useLiveStatus'
import { SignInPanel } from '@/features/attendance/SignInPanel'
import { formatTrainerConflict } from '@/lib/trainerConflicts'

// ─── Types ────────────────────────────────────────────────────────────────────

type DayEntry = { date: string; startTime: string; endTime: string }

type BookingFormData = {
  courseId: string
  bookingDate: string
  startTime: string
  endTime: string
  additionalDays: DayEntry[]
  places: number
  status: BookingStatus
  notes: string
  venueId: string | null
  attendeeIds: string[]
  trainerAssignments: TrainerAssignment[]
  deliveryMethod: DeliveryMethod
  meetingUrl: string
  meetingInstructions: string
}

const BOOKING_STATUSES: BookingStatus[] = ['Booked', 'Completed', 'Cancelled', 'Rejected']

const statusConfig: Record<BookingStatus, { pill: string; row: string; icon: React.ReactNode }> = {
  Booked:    { pill: 'bg-indigo-50 text-indigo-700 border-indigo-200', row: 'text-indigo-700', icon: <CalendarCheck size={13} /> },
  Completed: { pill: 'bg-green-50 text-green-700 border-green-200',   row: 'text-green-700',  icon: <CheckCircle2 size={13} /> },
  Cancelled: { pill: 'bg-gray-100 text-gray-500 border-gray-200',     row: 'text-gray-500',   icon: <Ban size={13} /> },
  Rejected:  { pill: 'bg-amber-50 text-amber-700 border-amber-200',   row: 'text-amber-700',  icon: <XCircle size={13} /> },
}

function Field({ id, label, children }: { id?: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  )
}

// ─── Attendee certificate row (Certificates tab) ───────────────────────────────

function AttendeeCertificateRow({
  attendee, cert, isThirdPartyCertified, onUpload,
}: {
  attendee: BookingAttendeeDto
  cert: BookingAttendeeCertificateDto | undefined
  isThirdPartyCertified: boolean
  onUpload: (trainingRecordId: string, file: File) => Promise<unknown>
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const trigger = () => {
    if (cert?.hasCertificate && !confirm('Replace the certificate currently on file with this new file?')) return
    fileInputRef.current?.click()
  }

  const handleFile = async (file: File | null) => {
    if (!file || !cert?.trainingRecordId) return
    setUploading(true)
    try { await onUpload(cert.trainingRecordId, file) } finally { setUploading(false) }
  }

  return (
    <div className="flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-gray-800 truncate">{attendee.firstName} {attendee.lastName}</span>
        {attendee.isWalkIn && <span className="text-xs text-gray-400 shrink-0">Walk-in</span>}
      </div>
      <div className="flex items-center gap-2.5 shrink-0">
        {cert?.hasCertificate && cert.certificateId ? (
          <>
            <a href={certificateViewUrl(cert.certificateId)} target="_blank" rel="noreferrer"
              title="View certificate" className="text-gray-400 hover:text-indigo-600 transition-colors">
              <Eye size={15} />
            </a>
            <a href={certificateDownloadUrl(cert.certificateId)} download
              title="Download certificate" className="text-gray-400 hover:text-indigo-600 transition-colors">
              <Download size={15} />
            </a>
            {isThirdPartyCertified && (
              <button type="button" onClick={trigger} disabled={uploading}
                title="Re-upload certificate" className="text-gray-400 hover:text-indigo-600 transition-colors disabled:opacity-50">
                <Upload size={15} />
              </button>
            )}
          </>
        ) : isThirdPartyCertified && cert?.trainingRecordId ? (
          <button type="button" onClick={trigger} disabled={uploading}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors disabled:opacity-50">
            <Upload size={13} /> {uploading ? 'Uploading…' : 'Upload'}
          </button>
        ) : (
          <span className="text-xs text-gray-300">
            {attendee.outcome === 'Failed'
              ? 'Failed'
              : attendee.attendanceStatus !== 'Present' ? 'Not attended' : 'Not yet issued'}
          </span>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden"
        onChange={e => { const f = e.target.files?.[0] ?? null; e.target.value = ''; handleFile(f) }} />
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BookingDetailPage() {
  const { formatDate } = useAdminDates()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: booking, isLoading } = useQuery({
    queryKey: ['booking', id],
    queryFn: () => adminApi.getBooking(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: pendingCertificates = [], isLoading: pendingCertificatesLoading } = useQuery({
    queryKey: ['booking-pending-third-party-certificates', id],
    queryFn: () => adminApi.getPendingThirdPartyCertificates(undefined, id).then(r => r.data),
    enabled: !!id && booking?.status === 'Completed' && booking?.courseIsThirdPartyCertified && !!booking?.certificatesIssuedAt,
  })

  const { data: attendeeCertificates = [], isLoading: attendeeCertificatesLoading } = useQuery({
    queryKey: ['booking-attendee-certificates', id],
    queryFn: () => adminApi.getBookingAttendeeCertificates(id!).then(r => r.data),
    enabled: !!id && booking?.status === 'Completed',
  })

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })
  const { data: trainers = [] } = useQuery({
    queryKey: ['trainers'],
    queryFn: () => adminApi.getTrainers().then(r => r.data),
  })
  const { data: staff = [] } = useQuery({
    queryKey: ['staff', booking?.clientId],
    queryFn: () => adminApi.getStaff(booking!.clientId).then(r => r.data),
    enabled: !!booking?.clientId,
  })

  const [form, setForm] = useState<BookingFormData | null>(null)
  const [showStatusMenu, setShowStatusMenu] = useState(false)
  const [selectedTab, setTab] = useState<'details' | 'attendees' | 'live' | 'certificates' | 'feedback'>('details')

  // The Live tab only exists when Live is switched on for this tenant (and the booking can still run a session).
  // Shares its cached query with LiveRoomCard, so this costs no extra request.
  const liveAvailable = !!booking && booking.status !== 'Cancelled' && booking.status !== 'Rejected'
  const { data: liveStatus } = useLiveStatus({ bookingId: id! }, !!id && liveAvailable)
  const showLiveTab = liveAvailable && !!liveStatus?.enabled
  const liveIsOpen = liveStatus?.delivery?.status === 'Open'
  // If the tab disappears while selected (e.g. the booking gets cancelled), fall back to Details rather than a blank page.
  const tab = selectedTab === 'live' && !showLiveTab ? 'details' : selectedTab

  const assignedTrainerIds = form?.bookingDate ? form.trainerAssignments.map(a => a.trainerId) : []
  const conflictQueries = useQueries({
    queries: assignedTrainerIds.map(tid => ({
      queryKey: ['trainer-conflicts', tid, form?.bookingDate, id],
      queryFn: () => adminApi.getTrainerConflicts(tid, form!.bookingDate, id).then(r => r.data),
      enabled: !!form?.bookingDate,
    })),
  })
  const conflictMap: Record<string, string[]> = {}
  assignedTrainerIds.forEach((tid, i) => {
    const conflicts = conflictQueries[i].data ?? []
    if (conflicts.length > 0) conflictMap[tid] = conflicts.map(formatTrainerConflict)
  })

  const { data: feedback } = useQuery({
    queryKey: ['booking-feedback', id],
    queryFn: () => adminApi.getBookingFeedback(id!).then(r => r.data),
    enabled: !!id,
  })

  // Initialise form once booking loads (only on first load)
  if (booking && !form) {
    setForm({
      courseId: booking.courseId,
      bookingDate: booking.bookingDate.slice(0, 10),
      startTime: booking.startTime ? booking.startTime.slice(0, 5) : '',
      endTime: booking.endTime ? booking.endTime.slice(0, 5) : '',
      additionalDays: (booking.additionalDays ?? []).map(d => ({
        date: d.date.slice(0, 10),
        startTime: d.startTime ? d.startTime.slice(0, 5) : '',
        endTime: d.endTime ? d.endTime.slice(0, 5) : '',
      })),
      places: booking.places,
      status: booking.status,
      notes: booking.notes ?? '',
      venueId: booking.venueId ?? null,
      attendeeIds: booking.attendees.map(a => a.staffMemberId),
      trainerAssignments: booking.trainerAssignments.map(t => ({ trainerId: t.trainerId, role: t.role })),
      deliveryMethod: booking.deliveryMethod,
      meetingUrl: booking.meetingUrl ?? '',
      meetingInstructions: booking.meetingInstructions ?? '',
    })
  }

  const toUpdatePayload = (data: BookingFormData) => ({
      ...data,
      notes: data.notes || null,
      venueId: data.deliveryMethod === 'Online' ? null : (data.venueId || null),
      startTime: data.startTime || null,
      endTime: data.endTime || null,
      additionalDays: data.additionalDays.map(d => ({
        date: d.date,
        startTime: d.startTime || null,
        endTime: d.endTime || null,
      })),
      meetingUrl: data.deliveryMethod === 'Online' ? (data.meetingUrl || null) : null,
      meetingInstructions: data.deliveryMethod === 'Online' ? (data.meetingInstructions || null) : null,
  })

  const updateMutation = useMutation({
    mutationFn: (data: BookingFormData) => adminApi.updateBooking(id!, toUpdatePayload(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking', id] })
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['bookings', booking?.clientId] })
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] })
      navigate(-1)
    },
  })

  const [issueResult, setIssueResult] = useState<{ trainingRecordsCreated: number; alreadyExisted: number; certificatesGenerated: number } | null>(null)

  // Completing is final (it creates training records and certificates), so it goes through a confirmation
  // modal and happens immediately rather than waiting for the page's Save button.
  const [completing, setCompleting] = useState(false)
  const [passingIds, setPassingIds] = useState<string[]>([])

  const completeMutation = useMutation({
    mutationFn: async () => {
      // Keep any edits made on the page: once completed, only the notes can change.
      await adminApi.updateBooking(id!, toUpdatePayload({ ...form!, status: booking!.status }))
      await adminApi.completeBooking(id!, passingIds)
      return adminApi.issueCertificates(id!).then(r => r.data)
    },
    onSuccess: (result) => {
      setIssueResult(result)
      setCompleting(false)
      setForm(null)   // re-initialised from the refreshed booking, now Completed
      setTab('certificates')
      queryClient.invalidateQueries({ queryKey: ['booking', id] })
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['bookings', booking?.clientId] })
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['booking-attendee-certificates', id] })
    },
  })

  const issueMutation = useMutation({
    mutationFn: () => adminApi.issueCertificates(id!).then(r => r.data),
    onSuccess: (result) => {
      setIssueResult(result)
      queryClient.invalidateQueries({ queryKey: ['booking', id] })
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['bookings', booking?.clientId] })
      queryClient.invalidateQueries({ queryKey: ['booking-attendee-certificates', id] })
      queryClient.invalidateQueries({ queryKey: ['booking-pending-third-party-certificates', id] })
    },
  })

  const completeProgress = useIssueProgress(id, completeMutation.isPending)
  const issueProgress = useIssueProgress(id, issueMutation.isPending)

  useTopBar(
    <Breadcrumb items={[
      { label: 'Client Bookings', to: '/admin/bookings' },
      { label: booking?.courseName ?? '…' },
    ]} />
  )

  if (isLoading || !form) {
    return <p className="text-gray-500">Loading…</p>
  }
  if (!booking) {
    return <p className="text-gray-500">Booking not found.</p>
  }

  const eligibleTrainers = trainers.filter(t =>
    t.isActive && (!form.courseId || t.qualifiedCourseIds.includes(form.courseId))
  )

  const setTrainerAssignments = (next: TrainerAssignment[]) =>
    setForm(f => f ? { ...f, trainerAssignments: next } : f)

  const toggleAttendee = (staffMemberId: string) => {
    setForm(f => {
      if (!f) return f
      return {
        ...f,
        attendeeIds: f.attendeeIds.includes(staffMemberId)
          ? f.attendeeIds.filter(x => x !== staffMemberId)
          : [...f.attendeeIds, staffMemberId],
      }
    })
  }

  const updateDay = (idx: number, field: keyof DayEntry, value: string) => {
    setForm(f => {
      if (!f) return f
      const days = f.additionalDays.map((d, i) => i === idx ? { ...d, [field]: value } : d)
      return { ...f, additionalDays: days }
    })
  }

  const addDay = () => {
    setForm(f => f ? { ...f, additionalDays: [...f.additionalDays, { date: '', startTime: '09:00', endTime: '' }] } : f)
  }

  const removeDay = (idx: number) => {
    setForm(f => f ? { ...f, additionalDays: f.additionalDays.filter((_, i) => i !== idx) } : f)
  }

  const leadCount = form.trainerAssignments.filter(a => a.role === 'Lead').length
  const staffingWarning = form.trainerAssignments.length > 0 && leadCount === 0
    ? 'No lead trainer assigned.'
    : null

  const isLocked = booking.status === 'Completed'
  const attendeeCount = form.attendeeIds.length
  const placesMismatch = attendeeCount > 0 && attendeeCount !== form.places
  const placesMismatchMsg = attendeeCount > form.places
    ? `${attendeeCount} attendees assigned but only ${form.places} place${form.places === 1 ? '' : 's'} booked — increase places or remove attendees.`
    : `${form.places} place${form.places === 1 ? '' : 's'} booked but only ${attendeeCount} attendee${attendeeCount === 1 ? '' : 's'} assigned.`
  // Shown on both the Details and Attendees tabs — it concerns the places field and the attendee list.
  const mismatchBanner = placesMismatch ? (
    <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-500" />
      <span>{placesMismatchMsg}</span>
    </div>
  ) : null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">
            {booking.clientName} ·{' '}
            {formatDate(booking.bookingDate + 'T00:00:00', {
              day: 'numeric', month: 'long', year: 'numeric',
            })}
          </p>
        </div>
        {booking.status === 'Completed' ? (
          <span
            title="A completed booking is final: it can't be reopened or cancelled. Attendance mistakes are corrected from the attendance list."
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium ${statusConfig.Completed.pill}`}
          >
            {statusConfig.Completed.icon}
            Completed
          </span>
        ) : (
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowStatusMenu(v => !v)}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer ${statusConfig[form?.status ?? booking.status].pill}`}
          >
            {statusConfig[form?.status ?? booking.status].icon}
            {form?.status ?? booking.status}
            <ChevronDown size={13} className="opacity-60" />
          </button>
          {showStatusMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowStatusMenu(false)} />
              <div className="absolute right-0 top-full mt-1.5 z-50 w-44 rounded-xl border border-gray-200 bg-white shadow-lg py-1 overflow-hidden">
                {BOOKING_STATUSES.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setShowStatusMenu(false)
                      if (booking.status === 'Completed' && s !== 'Completed' && booking.certificatesIssuedAt) {
                        const ok = confirm(
                          `This booking already has training records and/or certificates generated from when it was completed. Changing the status to ${s} will NOT delete them — they'll stay exactly as they are. Completing this booking again later will create new records rather than replacing these, so remove the existing ones first from the Certificates tab if that's what you want.\n\nChange status to ${s} anyway?`
                        )
                        if (!ok) return
                      }
                      if (s === 'Completed' && booking.status !== 'Completed') {
                        setPassingIds(form?.attendeeIds ?? [])
                        setCompleting(true)
                        return
                      }
                      setForm(f => f ? { ...f, status: s } : f)
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className={`${statusConfig[s].row}`}>{statusConfig[s].icon}</span>
                    <span className={`${statusConfig[s].row} font-medium`}>{s}</span>
                    {(form?.status ?? booking.status) === s && (
                      <Check size={13} className="ml-auto text-gray-400" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {completing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => !completeMutation.isPending && setCompleting(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Complete this booking?</h2>
            <p className="text-sm text-gray-600">
              This marks the booking as completed straight away. A training record{booking.courseIsThirdPartyCertified ? '' : ' and certificate'} is
              created for each attendee ticked below. Once completed, a booking can't be reopened or cancelled.
            </p>
            {form.attendeeIds.length === 0 ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                No attendees are assigned, so no training records will be created.
              </p>
            ) : (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Who passed?</p>
                <div className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
                  {staff.filter(m => form.attendeeIds.includes(m.id)).map(m => {
                    const selected = passingIds.includes(m.id)
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setPassingIds(ids => selected ? ids.filter(x => x !== m.id) : [...ids, m.id])}
                        className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
                          selected ? 'border-green-400 bg-green-50 text-green-800' : 'border-gray-200 bg-white text-gray-400 line-through'
                        }`}
                      >
                        {selected && <Check size={13} className="shrink-0 text-green-600" />}
                        <span className="truncate">{m.firstName} {m.lastName}</span>
                      </button>
                    )
                  })}
                </div>
                <p className="text-xs text-gray-500">Untick anyone who didn't attend or didn't pass.</p>
              </div>
            )}
            {completeMutation.isPending && <IssueProgressBar progress={completeProgress} />}
            {completeMutation.isError && (
              <p className="text-sm text-red-600">Couldn't complete the booking. Check the details on the page and try again.</p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={completeMutation.isPending} onClick={() => setCompleting(false)}>Cancel</Button>
              <Button type="button" disabled={completeMutation.isPending} onClick={() => completeMutation.mutate()}>
                {completeMutation.isPending ? 'Completing…' : 'Complete booking'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab switcher ── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit max-w-full overflow-x-auto [&>button]:shrink-0 [&>button]:whitespace-nowrap">
        <button
          onClick={() => setTab('details')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${tab === 'details' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Details
        </button>
        <button
          onClick={() => setTab('attendees')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${tab === 'attendees' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Users size={13} />
          Attendees
          <span className={`text-xs font-semibold ${placesMismatch ? 'text-amber-600' : 'text-gray-400'}`}>
            {attendeeCount}/{form.places}
          </span>
        </button>
        {showLiveTab && (
          <button
            onClick={() => setTab('live')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${tab === 'live' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Radio size={13} />
            Live
            {liveIsOpen && <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" title="A live session is open" />}
          </button>
        )}
        <button
          onClick={() => setTab('certificates')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${tab === 'certificates' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Award size={13} />
          Certificates
        </button>
        <button
          onClick={() => setTab('feedback')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${tab === 'feedback' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Star size={13} />
          Feedback
          {feedback && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
              {feedback.rating}
            </span>
          )}
        </button>
      </div>

      {tab === 'feedback' && (
        <div>
          {booking.status !== 'Completed' ? (
            <div className="text-center py-12 text-sm text-gray-400 bg-white border border-gray-200 rounded-xl">
              Feedback can only be submitted for completed bookings.
            </div>
          ) : feedback ? (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Star size={16} className="text-amber-400 fill-amber-400" />
                  Client feedback
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <Star key={i} size={16} className={i < feedback.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'} />
                  ))}
                  <span className="text-sm font-semibold text-gray-700 ml-1">{feedback.rating}/10</span>
                </div>
                {feedback.comment && (
                  <p className="text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 whitespace-pre-wrap">
                    {feedback.comment}
                  </p>
                )}
                <p className="text-xs text-gray-400">
                  Submitted {formatDate(feedback.submittedAt, { day: 'numeric', month: 'long', year: 'numeric' })}
                  {feedback.updatedAt && <span> · Edited {formatDate(feedback.updatedAt, { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-12 text-sm text-gray-400 bg-white border border-gray-200 rounded-xl">
              <Star size={28} className="mx-auto text-gray-200 mb-3" />
              No feedback submitted yet for this booking.
            </div>
          )}
        </div>
      )}

      {tab === 'certificates' && (
        booking.status !== 'Completed' ? (
          <div className="text-center py-12 text-sm text-gray-400 bg-white border border-gray-200 rounded-xl">
            <Award size={28} className="mx-auto text-gray-200 mb-3" />
            Certificates become available once this booking is completed.
          </div>
        ) : (<>
      {/* ── Certificate issuance panel ── */}
      {booking.status === 'Completed' && booking.courseIsThirdPartyCertified ? (
        <div className={`rounded-lg border px-4 py-4 flex items-start gap-3 ${
          booking.certificatesIssuedAt
            ? 'border-green-200 bg-green-50'
            : 'border-amber-200 bg-amber-50'
        }`}>
          <Award size={18} className={`shrink-0 mt-0.5 ${booking.certificatesIssuedAt ? 'text-green-500' : 'text-amber-500'}`} />
          <div className="flex-1">
            {booking.certificatesIssuedAt ? (
              <>
                <p className="text-sm font-medium text-green-800">Training records created</p>
                <p className="text-xs text-green-600 mt-0.5">
                  Completed on {formatDate(booking.certificatesIssuedAt, { day: 'numeric', month: 'long', year: 'numeric' })}.
                  {' '}{booking.courseThirdPartyProviderName || 'The provider'} issues the certificates for this course — upload them below once they arrive.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-amber-800">Session complete — training records not yet created</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {booking.courseThirdPartyProviderName || 'The provider'} issues certificates for this course, so CertSlate won't generate one —
                  but confirming here still creates training records for everyone marked Present, ready for their certificate to be uploaded later.
                </p>
              </>
            )}
            {issueMutation.isPending && <div className="mt-3"><IssueProgressBar progress={issueProgress} idleLabel="Issuing certificates…" /></div>}
          </div>
          {!booking.certificatesIssuedAt && (
            <Button
              size="sm"
              onClick={() => issueMutation.mutate()}
              disabled={issueMutation.isPending}
              className="shrink-0"
            >
              {issueMutation.isPending ? 'Confirming…' : 'Confirm completion'}
            </Button>
          )}
        </div>
      ) : booking.status === 'Completed' && (
        <div className={`rounded-lg border px-4 py-4 flex items-start gap-3 ${
          booking.certificatesIssuedAt
            ? 'border-green-200 bg-green-50'
            : 'border-indigo-200 bg-indigo-50'
        }`}>
          <Award size={18} className={`shrink-0 mt-0.5 ${booking.certificatesIssuedAt ? 'text-green-500' : 'text-indigo-500'}`} />
          <div className="flex-1">
            {booking.certificatesIssuedAt ? (
              <>
                <p className="text-sm font-medium text-green-800">Certificates issued</p>
                <p className="text-xs text-green-600 mt-0.5">
                  Issued on {formatDate(booking.certificatesIssuedAt, { day: 'numeric', month: 'long', year: 'numeric' })}
                  {issueResult && ` · ${issueResult.trainingRecordsCreated} record${issueResult.trainingRecordsCreated !== 1 ? 's' : ''} created, ${issueResult.certificatesGenerated} certificate${issueResult.certificatesGenerated !== 1 ? 's' : ''} generated`}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-indigo-800">Session complete — certificates not yet issued</p>
                <p className="text-xs text-indigo-600 mt-0.5">
                  Issue certificates to all attendees marked Present on the attendance register.
                </p>
              </>
            )}
            {issueMutation.isPending && <div className="mt-3"><IssueProgressBar progress={issueProgress} idleLabel="Issuing certificates…" /></div>}
          </div>
          {!booking.certificatesIssuedAt && (
            <Button
              size="sm"
              onClick={() => issueMutation.mutate()}
              disabled={issueMutation.isPending}
              className="shrink-0"
            >
              {issueMutation.isPending ? 'Issuing…' : 'Issue certificates'}
            </Button>
          )}
        </div>
      )}

      {/* Someone marked present after certificates were issued */}
      <MissingCertificatesBanner
        bookingId={booking.id}
        attendees={booking.attendees}
        certificates={attendeeCertificates}
        certificatesIssued={!!booking.certificatesIssuedAt}
        thirdParty={booking.courseIsThirdPartyCertified}
      />

      {/* ── Attendee certificates ── */}
      <div>
        <h2 className="text-sm font-medium text-gray-900 mb-2">Attendees</h2>
        {attendeeCertificatesLoading ? (
          <p className="text-sm text-gray-400 py-4 text-center bg-white border border-gray-200 rounded-xl">Loading…</p>
        ) : (
          <div className="space-y-1.5">
            {booking.attendees.map(a => (
              <AttendeeCertificateRow
                key={a.attendeeId}
                attendee={a}
                cert={attendeeCertificates.find(c => c.staffMemberId === a.staffMemberId)}
                isThirdPartyCertified={booking.courseIsThirdPartyCertified}
                onUpload={async (trainingRecordId, file) => {
                  await adminApi.uploadCertificate(trainingRecordId, file)
                  queryClient.invalidateQueries({ queryKey: ['booking-attendee-certificates', id] })
                  queryClient.invalidateQueries({ queryKey: ['booking-pending-third-party-certificates', id] })
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Third-party bulk upload ── */}
      {booking.status === 'Completed' && booking.courseIsThirdPartyCertified && booking.certificatesIssuedAt && pendingCertificates.length > 0 && (
        <div>
          <h2 className="text-sm font-medium text-gray-900 mb-2">Bulk upload certificates</h2>
          <ThirdPartyCertificateUploader
            pending={pendingCertificates.map(p => ({ id: p.trainingRecordId, name: p.staffMemberName, completedDate: p.completedDate }))}
            isLoading={pendingCertificatesLoading}
            providerName={booking.courseThirdPartyProviderName}
            emptyMessage="Nobody on this booking is waiting on a certificate."
            onUploaded={() => {
              queryClient.invalidateQueries({ queryKey: ['booking-pending-third-party-certificates', id] })
              queryClient.invalidateQueries({ queryKey: ['booking-attendee-certificates', id] })
            }}
            upload={(certId, file) => adminApi.uploadCertificate(certId, file)}
          />
        </div>
      )}
      </>)
      )}

      {tab === 'details' && (<>
      {booking.status === 'Rejected' && booking.rejectionReason && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
          <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
          <div>
            <p className="text-sm font-medium text-amber-800">Trainer rejected this booking</p>
            <p className="text-sm text-amber-700 mt-0.5">"{booking.rejectionReason}"</p>
            <p className="text-xs text-amber-600 mt-1">Reassign a trainer and change status back to Booked to reactivate.</p>
          </div>
        </div>
      )}

      {isLocked && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500">
          This booking is completed, so the course, date, places, venue/delivery method and trainer assignment are locked to keep them consistent with the training records already created. Notes can still be edited. Attendance and ID checks are corrected from the attendance list.
        </div>
      )}

      {/* ── Places / attendee mismatch banner ── */}
      {mismatchBanner}

      <form onSubmit={e => { e.preventDefault(); updateMutation.mutate(form) }} className="space-y-6">

        {/* ── Core details ── */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Booking details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field id="course" label="Course">
              <select
                id="course"
                value={form.courseId}
                disabled={isLocked}
                onChange={e => setForm(f => f ? { ...f, courseId: e.target.value, trainerAssignments: [] } : f)}
                className="h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                {courses.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.code ? `${c.code} — ` : ''}{c.name}
                  </option>
                ))}
              </select>
            </Field>

            {/* Day 1 */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Day 1</p>
              <div className="grid grid-cols-3 gap-3">
                <Field id="bookingDate" label="Date">
                  <Input id="bookingDate" type="date" value={form.bookingDate} disabled={isLocked}
                    onChange={e => setForm(f => f ? { ...f, bookingDate: e.target.value } : f)} />
                </Field>
                <Field id="startTime" label="Start time">
                  <Input id="startTime" type="time" step="300" value={form.startTime} disabled={isLocked}
                    onChange={e => setForm(f => f ? { ...f, startTime: e.target.value } : f)} />
                </Field>
                <Field id="endTime" label="End time">
                  <Input id="endTime" type="time" step="300" value={form.endTime} disabled={isLocked}
                    onChange={e => setForm(f => f ? { ...f, endTime: e.target.value } : f)} />
                </Field>
              </div>
            </div>

            {/* Additional days */}
            {form.additionalDays.map((day, idx) => (
              <div key={idx}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Day {idx + 2}</p>
                  {!isLocked && (
                    <button
                      type="button"
                      onClick={() => removeDay(idx)}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1 -mr-1 rounded cursor-pointer"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Date">
                    <Input type="date" value={day.date} disabled={isLocked}
                      onChange={e => updateDay(idx, 'date', e.target.value)} />
                  </Field>
                  <Field label="Start time">
                    <Input type="time" step="300" value={day.startTime} disabled={isLocked}
                      onChange={e => updateDay(idx, 'startTime', e.target.value)} />
                  </Field>
                  <Field label="End time">
                    <Input type="time" step="300" value={day.endTime} disabled={isLocked}
                      onChange={e => updateDay(idx, 'endTime', e.target.value)} />
                  </Field>
                </div>
              </div>
            ))}

            {!isLocked && (
              <button
                type="button"
                onClick={addDay}
                className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 transition-colors cursor-pointer"
              >
                <Plus size={14} />
                Add another day
              </button>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="places" label="Attendees">
                <div className="relative">
                  <Input
                    id="places" type="number" min="1" value={form.places === 0 ? '' : form.places} disabled={isLocked}
                    onChange={e => {
                      const v = e.target.value
                      setForm(f => f ? { ...f, places: v === '' ? 0 : parseInt(v) || 0 } : f)
                    }}
                    onBlur={() => setForm(f => f ? { ...f, places: Math.max(1, f.places) } : f)}
                    className={placesMismatch ? 'border-amber-400 focus-visible:ring-amber-400 pr-8' : ''}
                  />
                  {placesMismatch && (
                    <AlertTriangle size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-amber-500 pointer-events-none" />
                  )}
                </div>
              </Field>
              <Field id="notes" label="Notes">
                <textarea
                  id="notes"
                  value={form.notes}
                  onChange={e => setForm(f => f ? { ...f, notes: e.target.value } : f)}
                  placeholder="Optional"
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                />
              </Field>
            </div>
            <DeliveryMethodFields
              value={{ deliveryMethod: form.deliveryMethod, meetingUrl: form.meetingUrl, meetingInstructions: form.meetingInstructions }}
              onChange={next => setForm(f => f ? { ...f, ...next } : f)}
              disabled={isLocked}
              venueField={
                <VenueSelector
                  id="venue"
                  value={form.venueId}
                  onChange={venueId => setForm(f => f ? { ...f, venueId } : f)}
                  disabled={isLocked}
                />
              }
            />
          </CardContent>
        </Card>

        {/* ── Trainer assignment ── */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <UserCheck size={16} className="text-indigo-500" />
              Trainer assignment
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLocked ? (
              booking.trainerAssignments.length === 0 ? (
                <p className="text-sm text-gray-400">No trainer assigned.</p>
              ) : (
                <div className="space-y-1.5">
                  {booking.trainerAssignments.map(t => (
                    <div key={t.trainerId} className="flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm">
                      <span className="text-gray-800">{t.firstName} {t.lastName}</span>
                      <span className="text-xs text-gray-400">{t.role}</span>
                    </div>
                  ))}
                </div>
              )
            ) : eligibleTrainers.length === 0 ? (
              <NoEligibleTrainers
                hasCourse={!!form.courseId}
                hasAnyTrainers={trainers.some(t => t.isActive)}
              />
            ) : (
              <TrainerAssignmentFields
                trainers={eligibleTrainers}
                assignments={form.trainerAssignments}
                onChange={setTrainerAssignments}
                conflictMap={conflictMap}
              />
            )}
            {!isLocked && staffingWarning && (
              <div className="mt-3 flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
                <AlertTriangle size={14} className="shrink-0" />
                {staffingWarning}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Actions ── */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-3">
<Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </form>
      {booking.status === 'Completed' && <BookingHistoryCard bookingId={booking.id} />}
      </>)}

      {/* ── Live tab ── only reachable when Live is enabled for the tenant (see showLiveTab). */}
      {tab === 'live' && <LiveRoomCard target={{ bookingId: booking.id }} hidden={!liveAvailable} />}

      {/* ── Attendees tab ── Selection lives in the shared `form` state, so changes made here and
          on the Details tab are saved together by either Save button. */}
      {tab === 'attendees' && (<>
        {mismatchBanner}

        <SignInPanel
          target={{ bookingId: booking.id }}
          hidden={booking.status !== 'Booked'}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['booking', id] })}
        />

        {staff.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-gray-400">
              This client has no staff yet.{' '}
              <Link to={`/admin/clients/${booking.clientId}`} className="text-indigo-600 hover:underline">
                Add staff on the client's page
              </Link>{' '}
              to assign attendees.
            </CardContent>
          </Card>
        ) : (
          <Card className={placesMismatch ? 'border-amber-300' : ''}>
            <CardHeader className="pb-4">
              <CardTitle className="text-base flex items-center gap-2">
                Attendees
                <span className={`text-sm font-normal ${placesMismatch ? 'text-amber-600' : 'text-gray-400'}`}>
                  {attendeeCount} / {form.places}
                  {placesMismatch && <AlertTriangle size={13} className="inline ml-1 -mt-0.5" />}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {booking.status === 'Completed' ? (
                <CompletedAttendance
                  bookingId={booking.id}
                  attendees={booking.attendees}
                  certificates={attendeeCertificates}
                  certificatesIssued={!!booking.certificatesIssuedAt}
                  thirdParty={booking.courseIsThirdPartyCertified}
                />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                  {staff.map(m => {
                    const selected = form.attendeeIds.includes(m.id)
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggleAttendee(m.id)}
                        className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-left transition-colors ${
                          selected
                            ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {selected && <Check size={13} className="shrink-0 text-indigo-600" />}
                        <span className="truncate">{m.firstName} {m.lastName}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Completed bookings have no selectable attendees — corrections happen from the list itself */}
        {!isLocked && staff.length > 0 && (
          <div className="flex items-center justify-end">
            <Button type="button" onClick={() => updateMutation.mutate(form)} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </>)}
    </div>
  )
}

